import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react'

import IFindAllExtratoResponse from 'modules/pagamentos/domain/entities/IFindAllExtratoResponse'
import SelectExtratoImpl from 'modules/pagamentos/domain/usecases/SelectExtratoImpl'
import PagamentoRepository from 'modules/pagamentos/infra/repositories/PagamentoRepository'
import { useFrotista } from 'modules/user/hooks/useFrotista'

interface IFiltrosContextState {
  dateIni: Date
  dateFim: Date
  setDateIni(date: Date): void
  setDateFim(date: Date): void
  fetchExtrato(dateI: Date, dateF: Date): Promise<void>
  setRequestError(error: string): void
  extrato: IFindAllExtratoResponse
  loading: boolean
  setLoading: (value: boolean) => void
  requestError: string
}

const FiltrosContext = createContext<IFiltrosContextState>(
  {} as IFiltrosContextState,
)

export function useFiltro(): IFiltrosContextState {
  const context = useContext(FiltrosContext)

  if (!context) {
    throw new Error('useFiltro must be used within an FiltrosProvider')
  }

  return context
}

export const FiltrosProvider: React.FC = ({ children }) => {
  const motoristaRepository = useMemo(() => {
    return new PagamentoRepository()
  }, [])
  const selectExtrato = useMemo(() => {
    return new SelectExtratoImpl(motoristaRepository)
  }, [motoristaRepository])
  const [dateIni, setDateIni] = useState(new Date())
  const [dateFim, setDateFim] = useState(new Date())
  const [loading, setLoading] = useState(false)
  const [requestError, setRequestError] = useState('')
  const [extrato, setExtrato] = useState<IFindAllExtratoResponse>(
    {} as IFindAllExtratoResponse,
  )
  const { frotistaId } = useFrotista()

  const fetchExtratoFilter = useCallback(
    async (dateI: Date, dateF: Date) => {
      setLoading(true)
      setRequestError('')
      try {
        if (frotistaId) {
          const response = await selectExtrato.execute({
            data_inicio: dateI,
            data_fim: dateF,
            frotista_id: frotistaId,
          })

          setExtrato(response)
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          setRequestError(e.message)
        } else {
          setRequestError('Erro ao buscar extrato da conta')
        }
      } finally {
        setLoading(false)
      }
    },
    [selectExtrato, frotistaId],
  )

  useEffect(() => {
    fetchExtratoFilter(dateIni, dateFim)
  }, [dateIni, dateFim])

  const handleDateIni = useCallback((date: Date) => {
    setDateIni(date)
  }, [])

  const handleDateFim = useCallback((date: Date) => {
    setDateFim(date)
  }, [])

  return (
    <FiltrosContext.Provider
      value={{
        dateIni,
        dateFim,
        setDateIni: handleDateIni,
        setDateFim: handleDateFim,
        fetchExtrato: fetchExtratoFilter,
        extrato,
        loading,
        setLoading,
        requestError,
        setRequestError,
      }}
    >
      {children}
    </FiltrosContext.Provider>
  )
}
